import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GitBranch, ArrowLeft, Plus, Trash2, RefreshCw, Loader2,
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle2,
  BarChart3, Target, Users
} from 'lucide-react';
import { api } from '../api/client';
import { useTenant } from '../context/TenantContext';
import { toast } from 'sonner';

// ==========================================
// COMPARISON CHART
// ==========================================
const ComparisonChart = ({ scenarios, metric = 'demand' }) => {
  const maxValue = Math.max(...scenarios.map(s => s[metric]?.total || 0), 100);

  return (
    <div className="space-y-3">
      {scenarios.map((scenario, idx) => {
        const value = scenario[metric]?.total || 0;
        const width = Math.max((value / maxValue) * 100, 2);
        const isBaseline = scenario.isBaseline;
        
        return (
          <div key={scenario.id || idx} className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <span className={`font-medium ${isBaseline ? 'text-gray-600' : 'text-gray-900'}`}>
                {scenario.name}
                {isBaseline && <span className="ml-2 text-xs text-gray-400">(baseline)</span>}
              </span>
              <span className={`font-bold ${isBaseline ? 'text-gray-500' : 'text-purple-600'}`}>
                {value}h
              </span>
            </div>
            <div className="h-6 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  isBaseline ? 'bg-gray-400' : 'bg-purple-500'
                }`}
                style={{ width: `${width}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ==========================================
// DELTA INDICATOR
// ==========================================
const DeltaIndicator = ({ value, inverse = false }) => {
  if (value === undefined || value === null) return null;
  
  const isPositive = value > 0;
  const isGood = inverse ? !isPositive : isPositive;
  
  return (
    <span className={`flex items-center gap-1 text-sm font-medium ${
      isGood ? 'text-green-600' : 'text-red-600'
    }`}>
      {isPositive ? (
        <TrendingUp className="w-4 h-4" />
      ) : (
        <TrendingDown className="w-4 h-4" />
      )}
      {isPositive ? '+' : ''}{value}
    </span>
  );
};

// ==========================================
// SCENARIO COMPARISON CARD
// ==========================================
const ScenarioComparisonCard = ({ scenario, onRemove }) => {
  const isBaseline = scenario.isBaseline;
  const hasError = !!scenario.error;

  if (hasError) {
    return (
      <div className="p-4 bg-red-50 rounded-lg border border-red-200">
        <span className="text-red-700">Error loading scenario</span>
      </div>
    );
  }

  return (
    <div className={`p-4 rounded-xl border ${
      isBaseline ? 'bg-gray-50 border-gray-300' : 'bg-white border-gray-200'
    }`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className={`font-semibold ${isBaseline ? 'text-gray-600' : 'text-gray-900'}`}>
            {scenario.name}
          </h3>
          {isBaseline && (
            <span className="text-xs text-gray-500">Current state</span>
          )}
        </div>
        {!isBaseline && onRemove && (
          <button
            onClick={() => onRemove(scenario.id)}
            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Demand */}
        <div className="p-3 bg-purple-50 rounded-lg">
          <div className="flex items-center gap-2 text-purple-600 mb-1">
            <TrendingUp className="w-4 h-4" />
            <span className="text-xs font-medium">Total Demand</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-lg font-bold text-purple-900">
              {scenario.demand?.total || 0}h
            </span>
            {scenario.deltaFromBaseline && (
              <DeltaIndicator value={scenario.deltaFromBaseline.demandChange} inverse />
            )}
          </div>
        </div>

        {/* Capacity */}
        <div className="p-3 bg-green-50 rounded-lg">
          <div className="flex items-center gap-2 text-green-600 mb-1">
            <Users className="w-4 h-4" />
            <span className="text-xs font-medium">Total Capacity</span>
          </div>
          <span className="text-lg font-bold text-green-900">
            {scenario.capacity?.total || 0}h
          </span>
        </div>

        {/* Utilization */}
        <div className="p-3 bg-blue-50 rounded-lg">
          <div className="flex items-center gap-2 text-blue-600 mb-1">
            <Target className="w-4 h-4" />
            <span className="text-xs font-medium">Utilization</span>
          </div>
          <span className={`text-lg font-bold ${
            (scenario.utilizationPercent || 0) > 100 ? 'text-red-600' : 'text-blue-900'
          }`}>
            {scenario.utilizationPercent || 0}%
          </span>
        </div>

        {/* Bottlenecks */}
        <div className={`p-3 rounded-lg ${
          scenario.bottleneckCount > 0 ? 'bg-red-50' : 'bg-gray-50'
        }`}>
          <div className={`flex items-center gap-2 mb-1 ${
            scenario.bottleneckCount > 0 ? 'text-red-600' : 'text-gray-600'
          }`}>
            <AlertTriangle className="w-4 h-4" />
            <span className="text-xs font-medium">Bottlenecks</span>
          </div>
          <div className="flex items-center justify-between">
            <span className={`text-lg font-bold ${
              scenario.bottleneckCount > 0 ? 'text-red-900' : 'text-gray-900'
            }`}>
              {scenario.bottleneckCount || 0}
            </span>
            {scenario.deltaFromBaseline && (
              <DeltaIndicator value={scenario.deltaFromBaseline.bottleneckChange} inverse />
            )}
          </div>
        </div>
      </div>

      {/* Overloaded Projects */}
      {scenario.overloadedProjects > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <span className="text-xs text-gray-500">
            {scenario.overloadedProjects} project(s) contributing to bottlenecks
          </span>
        </div>
      )}
    </div>
  );
};

// ==========================================
// MAIN COMPARISON PAGE
// ==========================================
const ScenarioCompare = () => {
  const navigate = useNavigate();
  const { tenantId } = useTenant();

  const [loading, setLoading] = useState(false);
  const [scenarios, setScenarios] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [comparisonResult, setComparisonResult] = useState(null);
  const [includeBaseline, setIncludeBaseline] = useState(true);

  // Load available scenarios
  useEffect(() => {
    loadScenarios();
  }, [tenantId]);

  const loadScenarios = async () => {
    if (!tenantId) return;
    
    try {
      const response = await api.listScenarios(tenantId);
      setScenarios(response.data.scenarios || []);
    } catch (err) {
      console.error('Failed to load scenarios:', err);
    }
  };

  const runComparison = async () => {
    if (selectedIds.length === 0) {
      toast.error('Select at least one scenario to compare');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.compareScenarios(selectedIds, tenantId, includeBaseline);
      setComparisonResult(response.data);
    } catch (err) {
      console.error('Comparison failed:', err);
      toast.error('Failed to compare scenarios');
    } finally {
      setLoading(false);
    }
  };

  const handleAddScenario = (scenarioId) => {
    if (selectedIds.includes(scenarioId)) return;
    if (selectedIds.length >= 5) {
      toast.warning('Maximum 5 scenarios can be compared');
      return;
    }
    setSelectedIds(prev => [...prev, scenarioId]);
  };

  const handleRemoveScenario = (scenarioId) => {
    setSelectedIds(prev => prev.filter(id => id !== scenarioId));
    // Clear result if all scenarios removed
    if (selectedIds.length <= 1) {
      setComparisonResult(null);
    }
  };

  const getBestScenario = () => {
    if (!comparisonResult?.comparison) return null;
    
    // Find scenario with lowest bottleneck count (excluding baseline)
    const nonBaseline = comparisonResult.comparison.filter(s => !s.isBaseline && !s.error);
    if (nonBaseline.length === 0) return null;
    
    return nonBaseline.reduce((best, curr) => 
      (curr.bottleneckCount < best.bottleneckCount) ? curr : best
    );
  };

  const bestScenario = getBestScenario();

  return (
    <div className="min-h-screen bg-gray-50" data-testid="scenario-compare-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/scenarios')}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-xl">
                <GitBranch className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Compare Scenarios</h1>
                <p className="text-sm text-gray-500">Side-by-side scenario analysis</p>
              </div>
            </div>
          </div>

          <button
            onClick={runComparison}
            disabled={loading || selectedIds.length === 0}
            className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            data-testid="run-comparison-btn"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Run Comparison
          </button>
        </div>
      </div>

      <div className="p-6 grid grid-cols-12 gap-6">
        {/* Scenario Selection */}
        <div className="col-span-3 space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
              <h3 className="font-medium text-gray-900">Select Scenarios</h3>
              <p className="text-xs text-gray-500 mt-0.5">Up to 5 scenarios</p>
            </div>
            
            <div className="p-3 space-y-2 max-h-96 overflow-y-auto">
              {scenarios.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  <GitBranch className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No scenarios created yet</p>
                  <button
                    onClick={() => navigate('/scenarios')}
                    className="mt-2 text-sm text-purple-600 hover:underline"
                  >
                    Create a scenario
                  </button>
                </div>
              ) : (
                scenarios.map(scenario => {
                  const isSelected = selectedIds.includes(scenario.id);
                  return (
                    <button
                      key={scenario.id}
                      onClick={() => isSelected ? handleRemoveScenario(scenario.id) : handleAddScenario(scenario.id)}
                      className={`w-full p-3 rounded-lg border text-left transition-colors ${
                        isSelected 
                          ? 'border-purple-300 bg-purple-50' 
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`font-medium ${isSelected ? 'text-purple-900' : 'text-gray-900'}`}>
                          {scenario.name}
                        </span>
                        {isSelected ? (
                          <CheckCircle2 className="w-4 h-4 text-purple-600" />
                        ) : (
                          <Plus className="w-4 h-4 text-gray-400" />
                        )}
                      </div>
                      <span className="text-xs text-gray-500">
                        {scenario.projects?.length || 0} projects
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Options */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={includeBaseline}
                onChange={(e) => setIncludeBaseline(e.target.checked)}
                className="w-4 h-4 text-purple-600 rounded"
              />
              <div>
                <span className="text-sm font-medium text-gray-900">Include Baseline</span>
                <p className="text-xs text-gray-500">Compare against current state</p>
              </div>
            </label>
          </div>
        </div>

        {/* Comparison Results */}
        <div className="col-span-9 space-y-6">
          {!comparisonResult ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <GitBranch className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Select scenarios to compare</h3>
              <p className="text-gray-500 text-sm">
                Choose up to 5 scenarios and click "Run Comparison"
              </p>
            </div>
          ) : (
            <>
              {/* Best Scenario Highlight */}
              {bestScenario && (
                <div className="bg-green-50 rounded-xl border border-green-200 p-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                    <div>
                      <span className="font-semibold text-green-900">
                        Best Option: {bestScenario.name}
                      </span>
                      <p className="text-sm text-green-700 mt-0.5">
                        Lowest bottleneck count ({bestScenario.bottleneckCount}) with {bestScenario.utilizationPercent}% utilization
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Comparison Cards */}
              <div className="grid grid-cols-2 gap-4">
                {comparisonResult.comparison.map((scenario, idx) => (
                  <ScenarioComparisonCard
                    key={scenario.id || idx}
                    scenario={scenario}
                    onRemove={!scenario.isBaseline ? handleRemoveScenario : null}
                  />
                ))}
              </div>

              {/* Demand Comparison Chart */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center gap-2 mb-4">
                  <BarChart3 className="w-5 h-5 text-purple-600" />
                  <h3 className="font-semibold text-gray-900">Demand Comparison</h3>
                </div>
                <ComparisonChart 
                  scenarios={comparisonResult.comparison} 
                  metric="demand"
                />
              </div>

              {/* Summary Table */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
                  <h3 className="font-semibold text-gray-900">Summary Comparison</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-5 font-medium text-gray-700">Scenario</th>
                        <th className="text-right py-3 px-5 font-medium text-gray-700">Demand</th>
                        <th className="text-right py-3 px-5 font-medium text-gray-700">Capacity</th>
                        <th className="text-right py-3 px-5 font-medium text-gray-700">Utilization</th>
                        <th className="text-right py-3 px-5 font-medium text-gray-700">Bottlenecks</th>
                        <th className="text-center py-3 px-5 font-medium text-gray-700">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparisonResult.comparison.map((scenario, idx) => (
                        <tr key={scenario.id || idx} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-5">
                            <span className={`font-medium ${scenario.isBaseline ? 'text-gray-500' : 'text-gray-900'}`}>
                              {scenario.name}
                            </span>
                          </td>
                          <td className="py-3 px-5 text-right">{scenario.demand?.total || 0}h</td>
                          <td className="py-3 px-5 text-right">{scenario.capacity?.total || 0}h</td>
                          <td className="py-3 px-5 text-right">
                            <span className={scenario.utilizationPercent > 100 ? 'text-red-600' : ''}>
                              {scenario.utilizationPercent || 0}%
                            </span>
                          </td>
                          <td className="py-3 px-5 text-right">
                            <span className={scenario.bottleneckCount > 0 ? 'text-red-600 font-medium' : 'text-green-600'}>
                              {scenario.bottleneckCount || 0}
                            </span>
                          </td>
                          <td className="py-3 px-5 text-center">
                            {scenario.bottleneckCount === 0 ? (
                              <CheckCircle2 className="w-5 h-5 text-green-500 inline" />
                            ) : (
                              <AlertTriangle className="w-5 h-5 text-amber-500 inline" />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScenarioCompare;
